import { TooltipPosition } from '../schema/Tooltip.types';
import { UseTooltipPositionArguments } from '../schema/useTooltipPosition.types';
import { WindowDimensions } from 'mini-hooks';
import {
    ARROW_TOP_OFFSET,
    LENGTH_TRIANGLE,
    SCREEN_OFFSET,
    TRIANGLE_OFFSET,
    WIDTH_TRIANGLE,
} from './tooltipMeasures';

type CalculateTooltipPositionWithAnchorElementArguments = Pick<
    UseTooltipPositionArguments,
    'toolTip' | 'anchorRef' | 'placement'
> & { windowDimensions: WindowDimensions };

const calculateTooltipPositionWithAnchorElement = ({
    toolTip,
    anchorRef,
    placement,
    windowDimensions,
}: CalculateTooltipPositionWithAnchorElementArguments): TooltipPosition => {
    const documentWidth = windowDimensions.width;
    const { clientWidth: toolTipWidth, clientHeight: toolTipHeight } = toolTip.current;
    if (!toolTipWidth || !toolTipHeight) {
        return { x: 0, y: 0, arrowLeft: 8 };
    }

    const window = getWindow();
    const { scrollX, scrollY } = window;
    let arrowLeft;
    const {
        width: anchorElementWidth,
        height: anchorElementHeight,
        top: anchorElementTop,
        left: anchorElementLeft,
    } = anchorRef.current.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(anchorRef.current);
    let left;
    let top;
    const pointerOffsetLeft = TRIANGLE_OFFSET + WIDTH_TRIANGLE / 2;
    const toolTipAnchorDistance = LENGTH_TRIANGLE + ARROW_TOP_OFFSET;
    if (placement === 'left' || placement === 'right') {
        /**
         * total distance of tooltip from top of viewport should be equal to
         * distance of anchor element from top + mid of anchor element height - mid of tooltip height
         */
        top = anchorElementTop - toolTipHeight / 2 + anchorElementHeight / 2;
        if (placement === 'left') {
            arrowLeft = toolTipWidth;
            left =
                anchorElementLeft -
                toolTipWidth -
                toolTipAnchorDistance +
                parseFloat(computedStyle.paddingLeft);
        } else {
            arrowLeft = 0;
            left =
                anchorElementLeft +
                anchorElementWidth +
                toolTipAnchorDistance -
                parseFloat(computedStyle.paddingRight);
        }
    } else {
        arrowLeft = ARROW_TOP_OFFSET;
        left = anchorElementLeft + anchorElementWidth / 2 - pointerOffsetLeft;

        if (placement === 'bottom') {
            top =
                anchorElementTop +
                anchorElementHeight +
                toolTipAnchorDistance -
                parseFloat(computedStyle.paddingBottom);
        } else {
            top =
                anchorElementTop -
                toolTipHeight -
                toolTipAnchorDistance +
                parseFloat(computedStyle.paddingTop);
        }
    }
    left = Math.max(0, left);
    top = Math.max(0, top);

    const maxLeft = documentWidth - toolTipWidth - SCREEN_OFFSET;
    if (left > maxLeft) {
        arrowLeft = arrowLeft + left - maxLeft;
        left = maxLeft;
    }
    const [x, y] = [left + scrollX, top + scrollY];

    return {
        x,
        y,
        arrowLeft,
    };
};

export { calculateTooltipPositionWithAnchorElement };

// Exporting for testing purpose
export const getWindow = () => window;
